// Evals page (Observe → Evals, /insights): rubric quality on sampled
// production calls — per-guide summary, per-criterion bars, version-over-
// version comparison and the worst-scoring calls drill-down.
//
// Everything below the cross-guide table is scoped to ONE rubric (default:
// the active one). Criteria differ between rubric ids, so historical scores
// (e.g. companion_v2) are viewed by switching rubric, never blended in.

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { listGuides } from "../services/guides";
import type { CharacterProfile } from "../types/guide";
import { getGuideAnalytics, type GuideAnalyticsRow } from "../services/analytics";
import {
  backfillEvals,
  getEvalSummary,
  getRubric,
  listScoredCalls,
  type EvalSummary,
  type RubricCriterion,
  type ScoredCall,
} from "../services/evals";
import { GuideQualityTable } from "../components/evals/GuideQualityTable";
import { RubricSummary } from "../components/evals/RubricSummary";
import { VersionComparison } from "../components/evals/VersionComparison";
import { WorstCalls } from "../components/evals/WorstCalls";
import { CallDrawer } from "../components/analytics/CallDrawer";
import { RangePicker } from "../components/analytics/bits";
import { windowFromPreset, type RangePreset } from "../components/analytics/format";

export function EvalsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [guides, setGuides] = useState<CharacterProfile[]>([]);
  const [guideRows, setGuideRows] = useState<GuideAnalyticsRow[]>([]);
  const [guideId, setGuideId] = useState(searchParams.get("guideId") || "");
  // "" = the server's active rubric
  const [rubricId, setRubricId] = useState(searchParams.get("rubricId") || "");
  const [preset, setPreset] = useState<RangePreset>("30d");
  const [rubric, setRubric] = useState<RubricCriterion[]>([]);
  const [summary, setSummary] = useState<EvalSummary | null>(null);
  const [worst, setWorst] = useState<ScoredCall[]>([]);
  const [openCallId, setOpenCallId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [backfilling, setBackfilling] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const [refreshKey, setRefreshKey] = useState(0);

  const win = useMemo(() => windowFromPreset(preset), [preset]);

  const guideLabel = guides.find((g) => g.id === guideId)?.name || guideId;

  // Judge past calls the sampler skipped. This spends real judge budget, and
  // it scopes to the SELECTED companion even though the table above is
  // cross-guide — so name the companion and confirm before spending.
  async function runBackfill() {
    const cap = 200;
    if (
      !window.confirm(
        `Score up to ${cap} previously unjudged ${guideLabel} calls from the last ${preset}? ` +
          "Each one is a real judge call.",
      )
    ) {
      return;
    }
    setBackfilling(true);
    setNotice(null);
    try {
      const res = await backfillEvals({ guideId, from: win.from, to: win.to, limit: cap });
      // Cost visibility rides on every outcome, not just the happy path — an
      // operator sizing the next press needs the headroom regardless.
      const budget = `Live sampler has ${res.dailyBudgetRemaining} judge calls left today.`;
      let msg: string;
      if (res.failed > 0) {
        // Nothing is in flight for these — say so, or the operator waits for
        // scores that will never arrive and presses again.
        msg =
          `${res.failed} of ${res.failed + res.queued + res.deduplicated} ${guideLabel} calls could not be queued — check the Cloud Tasks queue configuration and server logs.` +
          (res.queued > 0 ? ` ${res.queued} did queue.` : "");
      } else if (res.queued === 0 && res.deduplicated > 0) {
        msg = `${guideLabel}: those ${res.deduplicated} calls are already queued from an earlier press.`;
      } else if (res.queued === 0) {
        msg = `Nothing to score — every eligible ${guideLabel} call in this range already has a rubric result.`;
      } else {
        const dupes = res.deduplicated > 0 ? ` (${res.deduplicated} already queued)` : "";
        msg =
          `Queued ${res.queued} ${guideLabel} call${res.queued === 1 ? "" : "s"} for judging${dupes}. ` +
          "Scores appear as the judge works through them.";
      }
      setNotice(`${msg} ${budget}`);
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  }

  useEffect(() => {
    listGuides()
      .then((gs) => {
        setGuides(gs);
        setGuideId((current) => current || gs[0]?.id || "");
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load companions"));
  }, []);

  // Labels/tooltips come from the definition of the rubric being shown.
  const shownRubricId = summary?.rubricId;
  useEffect(() => {
    if (!shownRubricId) return;
    let cancelled = false;
    getRubric(shownRubricId)
      .then((r) => !cancelled && setRubric(r.criteria))
      .catch(() => !cancelled && setRubric([]));
    return () => {
      cancelled = true;
    };
  }, [shownRubricId]);

  useEffect(() => {
    let cancelled = false;
    getGuideAnalytics({ from: win.from, to: win.to })
      .then((res) => {
        if (!cancelled) setGuideRows(res.items);
      })
      .catch(() => {
        if (!cancelled) setGuideRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [win, refreshKey]);

  useEffect(() => {
    if (!guideId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("guideId", guideId);
      if (rubricId) next.set("rubricId", rubricId);
      else next.delete("rubricId");
      return next;
    }, { replace: true });
    const scope = { rubricId: rubricId || undefined, from: win.from, to: win.to };
    Promise.allSettled([
      getEvalSummary(guideId, scope),
      listScoredCalls({ guideId, ...scope, limit: 20 }),
    ]).then(([s, w]) => {
      if (cancelled) return;
      setSummary(s.status === "fulfilled" ? s.value : null);
      setWorst(w.status === "fulfilled" ? w.value.items : []);
      if (s.status === "rejected") {
        const reason = s.reason;
        setError(reason instanceof Error ? reason.message : "Evals unavailable");
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [guideId, rubricId, win, setSearchParams, refreshKey]);

  // Older rubrics that hold this companion's scores while the shown one is
  // still empty (right after a rubric change) — point at them, don't blend.
  const historical =
    summary?.rubrics.filter((r) => r.rubricId !== summary.rubricId && r.count > 0) ?? [];

  return (
    <div className="list-page evals-page">
      <div className="list-toolbar">
        <h1>Evals</h1>
        <div className="spacer" />
        <select
          value={guideId}
          onChange={(e) => setGuideId(e.target.value)}
          aria-label="Companion"
          style={{ maxWidth: 240 }}
        >
          {guides.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name || g.id}
            </option>
          ))}
        </select>
        <RangePicker value={preset} onChange={setPreset} />
        <button
          className="btn btn-ghost btn-sm"
          onClick={runBackfill}
          disabled={backfilling || !guideId}
          title={`Judge past ${guideLabel} calls in this range that the sampler skipped`}
        >
          {backfilling ? "Queueing…" : `Score past ${guideLabel} calls`}
        </button>
        <button className="btn btn-ghost btn-sm" onClick={() => setRefreshKey((k) => k + 1)}>
          Refresh
        </button>
      </div>

      {error && <div className="callout">Evals unavailable ({error}).</div>}
      {notice && <div className="callout">{notice}</div>}

      <div className="panel-head">All companions</div>
      <GuideQualityTable rows={guideRows} selectedGuideId={guideId} onSelect={setGuideId} />

      {loading ? (
        <div className="state-box">Loading evals…</div>
      ) : summary ? (
        <>
          <div className="panel-head">
            Rubric quality · {summary.rubricId}
            <span className="spacer" />
            {summary.rubrics.length > 1 && (
              <select
                value={summary.rubricId}
                onChange={(e) => {
                  const picked = summary.rubrics.find((r) => r.rubricId === e.target.value);
                  setRubricId(picked?.active ? "" : e.target.value);
                }}
                aria-label="Rubric"
                title="Criteria differ between rubric versions, so each is shown on its own"
                style={{ maxWidth: 260 }}
              >
                {summary.rubrics.map((r) => (
                  <option key={r.rubricId} value={r.rubricId}>
                    {r.rubricId}
                    {r.active ? " (active)" : ""} · {r.count} scored
                  </option>
                ))}
              </select>
            )}
          </div>
          {summary.count === 0 && historical.length > 0 && (
            <div className="callout">
              No {guideLabel} calls scored on {summary.rubricId} in this range yet.{" "}
              {historical.map((r) => `${r.rubricId} has ${r.count}`).join(", ")} — pick it
              above to see those scores (rubric versions are never averaged together).
            </div>
          )}
          <RubricSummary
            overallAvg={summary.overallAvg}
            count={summary.count}
            criteria={summary.criteria}
            rubric={rubric}
          />

          <div className="panel-head">Version comparison</div>
          <VersionComparison versions={summary.versions} />

          <div className="panel-head">Worst-scoring calls</div>
          <WorstCalls items={worst} onOpen={setOpenCallId} />
        </>
      ) : (
        <div className="state-box">Pick a companion to see its rubric scores.</div>
      )}

      {openCallId && <CallDrawer callId={openCallId} onClose={() => setOpenCallId(null)} />}
    </div>
  );
}
